import React, { useEffect, useMemo, useState } from "react";
import { AppLayout } from "src/components/layouts";
import {
  FiMapPin,
  FiNavigation,
  FiRefreshCw,
  FiClock,
  FiUser,
  FiActivity,
} from "react-icons/fi";
import CustomSelect from "src/components/ui/CustomSelect";
import { QueryFunctionContext, useQuery } from "react-query";
import AdminService from "src/services/admin.services";
import FoodLoader from "src/components/Loader";
import { formatDistanceToNow } from "date-fns";

type AgentStatus = "online" | "offline" | "enroute" | "idle";

interface AgentLocation {
  id: string;
  name: string;
  phone: string;
  status: AgentStatus;
  lastSeen: string;
  currentTask?: string;
  totalDeliveries: number;
  successRate: number;
  lastLocation?: {
    latitude: number;
    longitude: number;
    address?: string;
  };
}

const adminService = new AdminService();
const PAGE_SIZE = 200;

const fetchAgents = async (ctx: QueryFunctionContext) => {
  const [, pageSize] = ctx.queryKey;
  const response = await adminService.getAgents(1, String(pageSize), "");
  if (!Array.isArray(response)) {
    throw new Error("Unexpected response format");
  }
  return response;
};

const LiveAgentTracking: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<AgentStatus | "all">("all");
  const [selectedAgent, setSelectedAgent] = useState<AgentLocation | null>(
    null
  );

  const {
    data: agents = [],
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useQuery(["liveAgents", PAGE_SIZE], fetchAgents, {
    keepPreviousData: true,
    refetchInterval: 30_000,
    refetchOnWindowFocus: false,
  });

  const agentLocations: AgentLocation[] = useMemo(() => {
    const deriveStatus = (agent: any): AgentStatus => {
      if (agent?.is_online || agent?.status === "online") return "online";
      if (agent?.current_delivery || agent?.current_order) return "enroute";
      if (agent?.account_status === "active" || agent?.is_active) return "idle";
      return "offline";
    };

    const buildLastSeen = (lastLogin?: string) => {
      if (!lastLogin) return "No recent activity";
      try {
        return `${formatDistanceToNow(new Date(lastLogin), {
          addSuffix: true,
        })}`;
      } catch {
        return "No recent activity";
      }
    };

    return agents.map((agent: any) => {
      const fullName =
        `${agent?.first_name || ""} ${agent?.last_name || ""}`.trim() ||
        agent?.name ||
        "Unknown Agent";

      const status = deriveStatus(agent);
      const currentLocation = agent?.current_location || agent?.location;
      let lastLocation: AgentLocation["lastLocation"] | undefined;

      if (
        currentLocation &&
        Array.isArray(currentLocation?.coordinates) &&
        currentLocation.coordinates.length >= 2
      ) {
        lastLocation = {
          latitude: Number(currentLocation.coordinates[1]),
          longitude: Number(currentLocation.coordinates[0]),
          address: currentLocation.address || "",
        };
      } else if (
        currentLocation &&
        typeof currentLocation.latitude === "number" &&
        typeof currentLocation.longitude === "number"
      ) {
        lastLocation = {
          latitude: currentLocation.latitude,
          longitude: currentLocation.longitude,
          address: currentLocation.address || "",
        };
      }

      return {
        id: agent?._id || agent?.id || fullName,
        name: fullName,
        phone: agent?.phone || "N/A",
        status,
        lastSeen: buildLastSeen(agent?.last_login),
        currentTask:
          agent?.current_delivery?.order_code ||
          agent?.current_order?.order_code ||
          agent?.current_task ||
          undefined,
        totalDeliveries:
          agent?.total_deliveries ||
          agent?.completed_deliveries ||
          agent?.delivery_count ||
          0,
        successRate: agent?.success_rate || agent?.rating || 0,
        lastLocation,
      };
    });
  }, [agents]);

  const filteredAgents = useMemo(() => {
    return agentLocations.filter((agent) => {
      const matchesSearch =
        agent.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        agent.phone.toLowerCase().includes(searchTerm.toLowerCase()) ||
        agent.id.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus =
        statusFilter === "all" ? true : agent.status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [agentLocations, searchTerm, statusFilter]);

  useEffect(() => {
    if (agentLocations.length === 0) {
      setSelectedAgent(null);
      return;
    }

    setSelectedAgent((prev) => {
      if (!prev) {
        return agentLocations[0];
      }
      const stillExists = agentLocations.find((agent) => agent.id === prev.id);
      return stillExists || agentLocations[0];
    });
  }, [agentLocations]);

  // KPIs for header cards
  const stats = useMemo(() => {
    const online = agentLocations.filter(
      (agent) => agent.status === "online" || agent.status === "enroute"
    ).length;
    const enroute = agentLocations.filter(
      (agent) => agent.status === "enroute"
    ).length;
    const idle = agentLocations.filter(
      (agent) => agent.status === "idle"
    ).length;
    const offline = agentLocations.filter(
      (agent) => agent.status === "offline"
    ).length;

    return [
      {
        label: "Online",
        value: online,
        icon: <FiActivity className="w-5 h-5" />,
        tone: "text-green-600 bg-green-50 border-green-200",
      },
      {
        label: "Enroute",
        value: enroute,
        icon: <FiNavigation className="w-5 h-5" />,
        tone: "text-blue-600 bg-blue-50 border-blue-200",
      },
      {
        label: "Idle",
        value: idle,
        icon: <FiClock className="w-5 h-5" />,
        tone: "text-amber-600 bg-amber-50 border-amber-200",
      },
      {
        label: "Offline",
        value: offline,
        icon: <FiUser className="w-5 h-5" />,
        tone: "text-gray-600 bg-gray-50 border-gray-200",
      },
    ];
  }, [agentLocations]);

  return (
    <AppLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-amber-900">
              Agent Live Locations
            </h1>
            <p className="text-amber-700 mt-2 max-w-2xl text-sm sm:text-base">
              Monitor all delivery agents in real-time. Filter by availability,
              select a rider, and review their latest coordinates before
              dispatching new orders.
            </p>
          </div>
          <button
            onClick={() => refetch()}
            className="self-start inline-flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg font-medium hover:bg-amber-700 transition-colors shadow-sm text-sm disabled:opacity-60 disabled:cursor-not-allowed"
            disabled={isFetching}
          >
            <FiRefreshCw className="w-4 h-4" />
            {isFetching ? "Refreshing..." : "Refresh Locations"}
          </button>
        </div>

        {/* KPI cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {stats.map((stat) => (
            <div
              key={stat.label}
              className={`p-4 rounded-xl border ${stat.tone} flex items-center justify-between`}
            >
              <div>
                <p className="text-sm font-medium">{stat.label}</p>
                <p className="text-2xl font-bold mt-1">{stat.value}</p>
              </div>
              <div className="p-2 rounded-lg bg-white shadow-sm text-amber-600">
                {stat.icon}
              </div>
            </div>
          ))}
        </div>

        {isError && (
          <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
            {error instanceof Error
              ? error.message
              : "Unable to fetch agents at this time."}
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-amber-100 p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <FiUser className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Search agent by name, ID or phone…"
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              />
            </div>
            <CustomSelect
              value={statusFilter}
              onChange={(value) =>
                setStatusFilter(value as AgentStatus | "all")
              }
              options={[
                { label: "All statuses", value: "all" },
                { label: "Online", value: "online" },
                { label: "Enroute", value: "enroute" },
                { label: "Idle", value: "idle" },
                { label: "Offline", value: "offline" },
              ]}
              placeholder="Filter by status"
            />
            <button
              onClick={() => {
                setSearchTerm("");
                setStatusFilter("all");
              }}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isLoading}
            >
              Clear filters
            </button>
          </div>
        </div>

        {/* Layout */}
        <div className="grid grid-cols-1 xl:grid-cols-[420px_1fr] gap-6">
          {/* Agent list */}
          <div className="bg-white border border-amber-100 rounded-2xl shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-amber-100 bg-amber-50">
              <h2 className="text-lg font-semibold text-amber-900">
                Available Agents
              </h2>
              <p className="text-sm text-amber-700">
                {isLoading
                  ? "Loading agents..."
                  : `${filteredAgents.length} agent${
                      filteredAgents.length === 1 ? "" : "s"
                    } ready for dispatch`}
              </p>
            </div>

            <div className="max-h-[560px] overflow-y-auto divide-y divide-amber-50">
              {isLoading ? (
                <div className="py-10 flex justify-center">
                  <FoodLoader text="Fetching agents" theme="amber" />
                </div>
              ) : filteredAgents.length === 0 ? (
                <div className="p-6 text-center text-gray-500">
                  No agents match your filters.
                </div>
              ) : (
                filteredAgents.map((agent) => {
                  const isActive = selectedAgent?.id === agent.id;
                  return (
                    <button
                      key={agent.id}
                      onClick={() => setSelectedAgent(agent)}
                      className={`w-full text-left px-4 py-4 transition ${
                        isActive
                          ? "bg-amber-50 border-l-4 border-amber-500"
                          : "hover:bg-amber-50"
                      }`}
                    >
                      <div className="flex justify-between items-start gap-3">
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="text-base font-semibold text-gray-900">
                              {agent.name}
                            </p>
                            <span
                              className={`text-xs px-2 py-0.5 rounded-full capitalize ${
                                agent.status === "online"
                                  ? "bg-green-100 text-green-700"
                                  : agent.status === "enroute"
                                  ? "bg-blue-100 text-blue-700"
                                  : agent.status === "idle"
                                  ? "bg-amber-100 text-amber-700"
                                  : "bg-gray-100 text-gray-500"
                              }`}
                            >
                              {agent.status}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600">{agent.phone}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            Last seen: {agent.lastSeen}
                          </p>
                        </div>
                        <div className="text-right text-sm text-gray-600">
                          <p>
                            Deliveries:{" "}
                            <span className="font-semibold">
                              {agent.totalDeliveries}
                            </span>
                          </p>
                          <p>
                            Success rate:{" "}
                            <span className="font-semibold">
                              {agent.successRate}%
                            </span>
                          </p>
                        </div>
                      </div>
                      {agent.currentTask && (
                        <div className="mt-3 text-xs bg-amber-100 text-amber-800 inline-flex px-2 py-1 rounded">
                          Current task: {agent.currentTask}
                        </div>
                      )}
                    </button>
                  );
                })
              )}
            </div>
          </div>

          {/* Detail & map panel */}
          <div className="bg-white border border-amber-100 rounded-2xl shadow-sm p-6">
            {!selectedAgent ? (
              <div className="h-full min-h-[420px] flex flex-col items-center justify-center text-center text-gray-500">
                <FiMapPin className="w-12 h-12 text-amber-300 mb-4" />
                <h3 className="text-lg font-semibold text-gray-800">
                  Select an agent to view live location
                </h3>
                <p className="text-sm">
                  Choose an agent from the list to preview their current
                  coordinates and status. The live map will appear here once an
                  agent is selected.
                </p>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">
                      {selectedAgent.name}
                    </h2>
                    <p className="text-sm text-gray-600">
                      {selectedAgent.phone}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <span
                      className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold capitalize ${
                        selectedAgent.status === "online"
                          ? "bg-green-100 text-green-700"
                          : selectedAgent.status === "enroute"
                          ? "bg-blue-100 text-blue-700"
                          : selectedAgent.status === "idle"
                          ? "bg-amber-100 text-amber-700"
                          : "bg-gray-100 text-gray-500"
                      }`}
                    >
                      {selectedAgent.status}
                    </span>
                    <span className="inline-flex items-center px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-semibold">
                      Success rate: {selectedAgent.successRate}%
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="bg-amber-50 border border-amber-100 rounded-lg p-3">
                    <p className="text-xs text-amber-700 uppercase">
                      Last update
                    </p>
                    <p className="text-sm font-semibold text-amber-900 mt-1">
                      {selectedAgent.lastSeen}
                    </p>
                  </div>
                  <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
                    <p className="text-xs text-blue-700 uppercase">
                      Completed deliveries
                    </p>
                    <p className="text-sm font-semibold text-blue-900 mt-1">
                      {selectedAgent.totalDeliveries}
                    </p>
                  </div>
                  <div className="bg-green-50 border border-green-100 rounded-lg p-3">
                    <p className="text-xs text-green-700 uppercase">
                      Current task
                    </p>
                    <p className="text-sm font-semibold text-green-900 mt-1">
                      {selectedAgent.currentTask || "None"}
                    </p>
                  </div>
                </div>

                <div>
                  <h3 className="text-sm font-semibold text-gray-800 mb-2">
                    Live location preview
                  </h3>
                  <div className="h-80 rounded-xl border-2 border-dashed border-amber-200 bg-amber-50 flex flex-col items-center justify-center text-center text-amber-700 px-6">
                    {selectedAgent.lastLocation ? (
                      <>
                        <p className="text-sm font-medium">
                          Latitude:{" "}
                          <span className="font-bold">
                            {selectedAgent.lastLocation.latitude.toFixed(6)}
                          </span>{" "}
                          | Longitude:{" "}
                          <span className="font-bold">
                            {selectedAgent.lastLocation.longitude.toFixed(6)}
                          </span>
                        </p>
                        {selectedAgent.lastLocation.address && (
                          <p className="text-sm mt-2">
                            {selectedAgent.lastLocation.address}
                          </p>
                        )}
                        <p className="text-xs mt-4 text-amber-600 max-w-md">
                          The interactive map will render here once live GPS
                          data is integrated. Use this container to embed Google
                          Maps or a Mapbox instance.
                        </p>
                      </>
                    ) : (
                      <p className="text-sm">
                        No recent coordinates available for this agent. The map
                        will update automatically once they come online.
                      </p>
                    )}
                  </div>
                </div>

                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm text-gray-700">
                  <h3 className="font-semibold text-gray-900 mb-2">
                    Integration notes
                  </h3>
                  <ul className="list-disc list-inside space-y-1 text-sm">
                    <li>
                      Replace the placeholder dataset with the live agent
                      location endpoint when it becomes available.
                    </li>
                    <li>
                      Invoke the location polling logic (every 10–15 seconds) to
                      keep coordinates fresh while an agent is selected.
                    </li>
                    <li>
                      Embed your preferred map provider inside the highlighted
                      container above.
                    </li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  );
};

export default LiveAgentTracking;
